import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiMail, FiPhone, FiMapPin, FiBriefcase, FiUser, FiCalendar, FiCheck, FiTrash2 } from 'react-icons/fi';
import { UseToast } from '../../hooks/UseToast';
import Badge from '../../components/ui/Badge';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const MessageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = UseToast();
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMessage();
  }, [id]);

  const fetchMessage = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/service-messages/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      
      console.log('Message API Response:', data); // ✅ Debug
      
      if (data.success) {
        setMessage(data.data);
        if (data.data.status === 'new') {
          handleStatusChange(data.data._id, 'contacted');
        }
      } else {
        toast.error(data.message || 'Message not found');
        navigate('/messages');
      }
    } catch (error) {
      toast.error('Failed to load message');
      console.error('Fetch error:', error);
      navigate('/messages');
    }
    setLoading(false);
  };

  const handleStatusChange = async (messageId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/service-messages/${messageId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(`Marked as ${newStatus}!`);
        setMessage({...message, status: newStatus });
      } else {
        toast.error(data.message || 'Update failed');
      }
    } catch (error) {
      toast.error('Something went wrong');
      console.error('Status update error:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/service-messages/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Message deleted successfully');
        navigate('/messages');
      } else {
        toast.error(data.message || 'Delete failed');
      }
    } catch (error) {
      toast.error('Something went wrong');
      console.error('Delete error:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      new: { color: '#F59E0B', bg: '#FEF3C7', label: 'New' },
      contacted: { color: '#3B82F6', bg: '#DBEAFE', label: 'Contacted' },
      closed: { color: '#10B981', bg: '#D1FAE5', label: 'Closed' }
    };
    return statusMap[status] || statusMap.new;
  };

  // ✅ Fixed: Safe date formatting
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: '#64748B', fontFamily: 'Outfit, sans-serif' }}>
        Loading message...
      </div>
    );
  }

  if (!message) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: '#64748B', fontFamily: 'Outfit, sans-serif' }}>
        Message not found
      </div>
    );
  }

  const badge = getStatusBadge(message.status);

  return (
    <>
      <style>{`
.detail-page { font-family: 'Outfit', sans-serif; }
.back-btn { display: inline-flex; align-items: center; gap: 8px; padding: 10px 16px; background: #F1F5F9; border: none; border-radius: 10px; color: #475569; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.3s ease; margin-bottom: 20px; }
.back-btn:hover { background: #E2E8F0; }
.detail-card { background: #FFF; border-radius: 16px; padding: 30px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); margin-bottom: 24px; }
.detail-card-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; padding-bottom: 20px; border-bottom: 1px solid #E2E8F0; flex-wrap: wrap; gap: 16px; }
.detail-title { font-size: 24px; font-weight: 800; color: #0F172A; margin: 0; }
.detail-actions { display: flex; gap: 12px; }
.action-button { padding: 10px 20px; border: none; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.3s ease; display: flex; align-items: center; gap: 8px; font-family: 'Outfit', sans-serif; }
.btn-primary { background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%); color: #FFF; }
.btn-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(59, 130, 246, 0.3); }
.btn-danger { background: #FEE2E2; color: #EF4444; }
.btn-danger:hover { background: #EF4444; color: #FFF; }
.info-section { margin-bottom: 32px; }
.section-title { font-size: 18px; font-weight: 700; color: #0F172A; margin: 0 0 16px 0; display: flex; align-items: center; gap: 10px; }
.info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
.info-item { display: flex; align-items: flex-start; gap: 12px; }
.info-icon { width: 40px; height: 40px; border-radius: 10px; background: #EFF6FF; display: flex; align-items: center; justify-content: center; color: #3B82F6; font-size: 18px; flex-shrink: 0; }
.info-content label { font-size: 13px; color: #64748B; font-weight: 600; display: block; margin-bottom: 4px; }
.info-content p { font-size: 15px; color: #0F172A; font-weight: 600; margin: 0; word-break: break-word; }
.message-box { background: #F8FAFC; padding: 20px; border-radius: 12px; border-left: 4px solid #3B82F6; }
.message-box p { font-size: 15px; color: #334155; line-height: 1.8; margin: 0; white-space: pre-wrap; }
        @media (max-width: 768px) {
    .info-grid { grid-template-columns: 1fr; }
    .detail-card-header { flex-direction: column; align-items: flex-start; }
    .detail-actions { width: 100%; }
    .action-button { flex: 1; }
        }
      `}</style>
      <div className="detail-page">
        <button className="back-btn" onClick={() => navigate('/messages')}>
          <FiArrowLeft /> Back to Messages
        </button>

        <div className="detail-card">
          <div className="detail-card-header">
            <div>
              <h1 className="detail-title">Message from {message.name || 'N/A'}</h1>
              <div style={{ marginTop: '8px' }}>
                <Badge color={badge.color} bg={badge.bg}>{badge.label}</Badge>
              </div>
            </div>
            <div className="detail-actions">
              {message.status === 'new' && (
                <button 
                  className="action-button btn-primary"
                  onClick={() => handleStatusChange(message._id, 'contacted')}
                >
                  <FiCheck /> Mark as Contacted
                </button>
              )}
              {message.status === 'contacted' && (
                <button 
                  className="action-button btn-primary"
                  onClick={() => handleStatusChange(message._id, 'closed')}
                >
                  <FiCheck /> Mark as Closed
                </button>
              )}
              <button 
                className="action-button btn-danger"
                onClick={handleDelete}
              >
                <FiTrash2 /> Delete
              </button>
            </div>
          </div>

          <div className="info-section">
            <h3 className="section-title"><FiUser /> Customer Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <div className="info-icon"><FiUser /></div>
                <div className="info-content">
                  <label>Name</label>
                  <p>{message.name || 'N/A'}</p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon"><FiMail /></div>
                <div className="info-content">
                  <label>Email</label>
                  <p>{message.email || 'N/A'}</p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon"><FiPhone /></div>
                <div className="info-content">
                  <label>Phone</label>
                  <p>{message.phone || 'N/A'}</p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon"><FiMapPin /></div>
                <div className="info-content">
                  <label>Location</label>
                  <p>{message.location || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="info-section">
            <h3 className="section-title"><FiBriefcase /> Service Enquiry</h3>
            <div className="info-grid">
              <div className="info-item">
                <div className="info-icon"><FiBriefcase /></div>
                <div className="info-content">
                  <label>Service</label>
                  <p>{message.service || 'N/A'}</p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon"><FiUser /></div>
                <div className="info-content">
                  <label>Business Type</label>
                  <p>{message.business || 'N/A'}</p>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon"><FiCalendar /></div>
                <div className="info-content">
                  <label>Received On</label>
                  <p>{formatDate(message.createdAt)}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="info-section">
            <h3 className="section-title"><FiMail /> Message</h3>
            <div className="message-box">
              <p>{message.message || 'No message content'}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MessageDetail;